/**
 * 日志服务实现
 * 提供全应用统一的日志记录工具
 *
 * 核心功能：
 * 1. 支持多级别日志记录（DEBUG, INFO, WARN, ERROR）
 * 2. 提供分类记录和标签过滤
 * 3. 可配置时间戳和额外数据
 * 4. 支持全局启用/禁用和级别设置
 * 5. 提供特定模块的预配置日志函数
 */
/**
 * 日志级别枚举
 */
export var LogLevel;
(function (LogLevel) {
    LogLevel["DEBUG"] = "debug";
    LogLevel["INFO"] = "info";
    LogLevel["WARN"] = "warn";
    LogLevel["ERROR"] = "error";
})(LogLevel || (LogLevel = {}));
/**
 * 日志服务类
 * 实现 ILoggerService 接口
 */
export class LoggerService {
    /**
     * 私有构造函数，防止直接实例化
     */
    constructor() {
        // 构造函数逻辑（如果有）
    }
    /**
     * 获取单例实例
     */
    static getInstance() {
        if (!LoggerService.instance) {
            LoggerService.instance = new LoggerService();
        }
        return LoggerService.instance;
    }
    /**
     * 设置日志级别
     */
    static setLevel(level) {
        this.level = level;
    }
    /**
     * 启用日志
     */
    static enable() {
        this.isEnabled = true;
    }
    /**
     * 禁用日志
     */
    static disable() {
        this.isEnabled = false;
    }
    /**
     * 检查是否应记录给定级别的日志
     */
    static shouldLog(level) {
        if (!this.isEnabled)
            return false;
        const levels = Object.values(LogLevel);
        const currentIndex = levels.indexOf(this.level);
        const messageIndex = levels.indexOf(level);
        return messageIndex >= currentIndex;
    }
    /**
     * 格式化日志消息
     */
    formatMessage(message, context) {
        const parts = [];
        // 添加时间戳
        const now = new Date();
        const timestamp = now.toISOString().split('T')[1].slice(0, 8);
        parts.push(`[${timestamp}]`);
        // 添加标签
        if (context?.tag) {
            parts.push(`[${context.tag}]`);
        }
        else {
            parts.push('[APP]'); // 默认标签
        }
        // 添加消息
        parts.push(message);
        return parts.join(' ');
    }
    /**
     * 记录调试级别日志
     * 实现 ILoggerService 接口方法
     */
    debug(message, context) {
        if (!LoggerService.shouldLog(LogLevel.DEBUG))
            return;
        const _formattedMessage = this.formatMessage(message, context);
    }
    /**
     * 记录信息级别日志
     * 实现 ILoggerService 接口方法
     */
    info(message, context) {
        if (!LoggerService.shouldLog(LogLevel.INFO))
            return;
        const _formattedMessage = this.formatMessage(message, context);
    }
    /**
     * 记录警告级别日志
     * 实现 ILoggerService 接口方法
     */
    warn(message, context) {
        if (!LoggerService.shouldLog(LogLevel.WARN))
            return;
        const _formattedMessage = this.formatMessage(message, context);
    }
    /**
     * 记录错误级别日志
     * 实现 ILoggerService 接口方法
     */
    error(message, context) {
        if (!LoggerService.shouldLog(LogLevel.ERROR))
            return;
        const _formattedMessage = this.formatMessage(message, context);
        // 2. Call the error reporter if configured
        if (LoggerService.errorReporter) {
            let reportableError;
            // Check if the provided error is already an AppError
            if (context?.error &&
                typeof context.error === 'object' &&
                'code' in context.error &&
                'type' in context.error) {
                reportableError = context.error;
            }
            else if (context?.data?.error instanceof Object &&
                'code' in context.data.error &&
                'type' in context.data.error) {
                // Check if AppError was passed via context.data
                reportableError = context.data.error;
            }
            if (reportableError) {
                try {
                    // Pass AppError and any additional context/data
                    LoggerService.errorReporter(reportableError, { message, ...context });
                }
                catch (reportError) {
                    // 移除了console.error调用
                }
            }
        }
    }
    /**
     * 设置错误报告函数
     * @param reporter - 实现错误上报逻辑的函数
     */
    static setErrorReporter(reporter) {
        this.errorReporter = reporter;
    }
    /**
     * 创建特定领域的日志记录器
     * 取代原来的静态对象
     */
    createDomainLogger(domain) {
        return {
            debug: (message, context) => {
                this.debug(message, { ...context, tag: domain });
            },
            info: (message, context) => {
                this.info(message, { ...context, tag: domain });
            },
            warn: (message, context) => {
                this.warn(message, { ...context, tag: domain });
            },
            error: (message, context) => {
                this.error(message, { ...context, tag: domain });
            },
            setLevel: (level) => {
                this.setLevel(level);
            },
            addTransport: (transport) => {
                this.addTransport(transport);
            },
        };
    }
    /**
     * 获取翻译领域的日志器
     */
    getTranslationLogger() {
        return this.createDomainLogger('TRANSLATION');
    }
    /**
     * 获取文档领域的日志器
     */
    getDocumentLogger() {
        return this.createDomainLogger('DOCUMENT');
    }
    /**
     * 实现可选的setLevel方法
     */
    setLevel(level) {
        // 将字符串级别映射到枚举
        const levelMap = {
            debug: LogLevel.DEBUG,
            info: LogLevel.INFO,
            warn: LogLevel.WARN,
            error: LogLevel.ERROR,
        };
        LoggerService.setLevel(levelMap[level]);
    }
    /**
     * 实现addTransport方法
     */
    addTransport(_transport) {
        // 这是一个占位实现，未来可以扩展为支持多种传输方式
    }
}
// 是否启用日志
LoggerService.isEnabled = true;
// 当前日志级别 (低于此级别的日志将被忽略)
LoggerService.level = LogLevel.DEBUG;
// 错误报告函数
LoggerService.errorReporter = null;
//# sourceMappingURL=logger.service.js.map